import {useState} from 'react';

import {CollapsibleCard} from 'shells/card/collapsible-card';
import {CodeBlock} from 'shells/code-block/code-block';
import {Stack} from 'shells/layout/stack/stack';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import {DomainFeatureStatusKey} from 'nutshell-graphql-types';
import type {GetAnalyticsStatus_analyticsStatus as AnalyticsStatus} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {useGetInstanceInfo} from '../../core/use-get-instance-info';
import {useGetDomainFeatureStatuses} from '../../setup/domains/graphql/hooks/use-get-domain-feature-statuses';

import {CopyableLinkWrapper} from './copyable-link-wrapper';
import {EmbedSnippetText} from './embed-snippet-text';
import {SetUpDomainBanner} from './setup-domain-banner';

interface Props {
    analyticsStatus: AnalyticsStatus;
}

export function EmbedAnalyticsModalContent(props: Props) {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const {sessionInstance} = useGetInstanceInfo();
    const instanceId = sessionInstance && sessionInstance.instanceId;

    const {domainFeatureStatuses} = useGetDomainFeatureStatuses();
    const dnsDomain =
        domainFeatureStatuses.length > 0 &&
        domainFeatureStatuses.find(
            (domainFeatureStatus) => domainFeatureStatus.key === DomainFeatureStatusKey.bootSnippet
        );
    const hasConfiguredDnsDomain = dnsDomain && dnsDomain.fqdn;

    const embeddedDomain = props.analyticsStatus.embeddedDomain;

    const codeBlock = (
        <CodeBlock
            includeCopyButton={true}
            height={230}
            value={props.analyticsStatus.trackingScript}
            label='Embed code'
            name='embedCode'
            onCopy={() => {
                analyticsClient.track(analyticsClient.EVENTS.TRACK_ANALYTICS_EMBED_CODE_COPIED);
            }}
        />
    );

    return (
        <StackLegacy isFullWidth={true} spacing={16}>
            {!hasConfiguredDnsDomain && SetUpDomainBanner}
            {EmbedSnippetText}
            {codeBlock}
            <CollapsibleCard
                padding={12}
                hasBorder={true}
                fitContent={false}
                fitHeight={true}
                hasShadow={true}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                header={
                    <Body size='normal' color='dark' isBold={true}>
                        Using Wordpress?
                    </Body>
                }
            >
                <Stack spacing={8}>
                    <CopyableLinkWrapper
                        type='LINK'
                        value='http://wordpress.org/plugins/nutshell-analytics/'
                        count={1}
                    />
                    {NutClientConfig.hasWordPressForBootSnippetFF && (
                        <>
                            <CopyableLinkWrapper type='ACCOUNT_ID' value={instanceId} count={2} />
                            <CopyableLinkWrapper
                                type='TOKEN'
                                value={props.analyticsStatus.authTokenForSnippet}
                                count={3}
                            />
                            <CopyableLinkWrapper type='DOMAIN' value={embeddedDomain} count={4} />
                        </>
                    )}
                </Stack>
            </CollapsibleCard>
        </StackLegacy>
    );
}
